import {
  Dialog,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { CloseOutlined } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import {
  getFileDataSampleRecords,
  getSampleRecords,
  getSampleRecordsForCustomQuery,
  getSampleRecordsForMySQL,
} from '../../api/dataset';
import { NotificationDialog } from '../CommonFunctions/DialogComponents';
import { TAlert } from '../../types/types';
interface ISampleRecordPopup {
  workspaceId: string;
  connectionId: string;
  recordCount: number;
  database: string;
  schema: string;
  tablename: string;
  vendor: string;
  open: boolean;
  flatfileId: string | null;
  customQuery?: string | null;
  onClose: () => void;
}
type GenericObject = {
  [key: string]: string | number | boolean | null;
};
function SampleRecordPopUp({
  workspaceId,
  connectionId,
  recordCount = 100,
  database,
  schema,
  tablename,
  open,
  vendor,
  flatfileId,
  onClose,
  customQuery,
}: ISampleRecordPopup) {
  const [data, setData] = useState<GenericObject[]>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState<TAlert | null>(null);
  useEffect(() => {
    if (!open) return;
    const fetchData = async () => {
      if (flatfileId) {
        const result = await getFileDataSampleRecords({
          flatFileId: flatfileId,
          table: '',
          workspaceId: '',
        }); // table,workspaceId are optional
        if (result.isError) {
          setAlert({
            severity: 'error',
            message: result.message,
          });
          setLoading(false);
          return;
        }

        if (result.data) {
          const firstRow = result.data[0];
          if (!firstRow) {
            setColumns([]);
            setLoading(false);
            return;
          }
          const columns = Object.keys(firstRow);
          setColumns(columns);
          setData(result.data);
          setLoading(false);
        } else {
          setLoading(false);
        }
      } else if (customQuery) {
        const result = await getSampleRecordsForCustomQuery({
          workspaceId: workspaceId,
          connectionValue: connectionId,
          num: 250,
          data: customQuery,
        });
        if (result.isError) {
          setAlert({
            severity: 'error',
            message: result.message,
          });
          setLoading(false);
          return;
        }
        if (result.data) {
          if (result.data) {
            const firstRow = result.data[0];
            if (!firstRow) {
              setColumns([]);
              setLoading(false);
              return;
            }
            const columns = Object.keys(firstRow);
            setColumns(columns);
            setData(result.data);
            setLoading(false);
          } else {
            setLoading(false);
          }
        } else {
          setLoading(false);
        }
      } else if (vendor === 'mysql' || vendor === 'mariadb' || vendor === 'clickhouse') {
        // url = `sample-records?workspaceId=${props.selectedWorkSpace}&databaseId=${connectionId}&recordCount=100&database=${databaseName}&table=${table}`;

        const result = await getSampleRecordsForMySQL({
          workspaceId: workspaceId,
          databaseId: connectionId,
          database,
          table: tablename,
        });

        if (result.isError) {
          setAlert({
            severity: 'error',
            message: result.message,
          });
          setLoading(false);
          return;
        }

        if (result.data) {
          const firstRow = result.data[0];
          if (!firstRow) {
            setColumns([]);
            return;
          }
          const columns = Object.keys(firstRow);
          setColumns(columns);
          setData(result.data);
          setLoading(false);
        } else {
          setLoading(false);
        }
      } else {
        const result = await getSampleRecords({
          workspaceId: workspaceId,
          databaseId: connectionId,
          database,
          table: tablename,
          schema: schema,
        });
        if (result.isError) {
          setAlert({
            severity: 'error',
            message: result.message,
          });
          setLoading(false);
          return;
        }
        if (result.data) {
          if (result.data) {
            const firstRow = result.data[0];
            if (!firstRow) {
              setColumns([]);
              setLoading(false);
              return;
            }
            const columns = Object.keys(firstRow);
            setColumns(columns);
            setData(result.data);
            setLoading(false);
          } else {
            setLoading(false);
          }
        } else {
          setLoading(false);
        }
      }
    };
    fetchData();
  }, [open]);
  return (
    <>
      <Dialog
        open={open}
        maxWidth="xl"
        fullWidth={true}
        PaperProps={{
          sx: {
            minHeight: '90%',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            flexDirection: 'row',
            columnGap: '2rem',
            justifyContent: 'space-between',
            fontSize: '16px',
            alignItems: 'center',
          }}
        >
          <p>Table: {tablename}</p>
          <p>Rows Displayed: {data.length}</p>
          <CloseOutlined onClick={onClose} style={{ marginTop: '-7px', cursor: 'pointer' }} />
        </DialogTitle>
        <DialogContent
          sx={{
            maxWidth: loading ? '100%' : 'fit-content',
            '::-webkit-scrollbar': {
              width: '5px',
              height: '5px',
            },
          }}
        >
          <div
            style={{
              overflowX: 'auto',
              overflowY: 'auto',
              maxHeight: '60vh',
            }}
          >
            {loading ? (
              <div
                className="loading-container"
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                }}
              >
                <div className="user-spinner"></div>
              </div>
            ) : columns.length > 0 ? (
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {columns.map((column: string, i: number) => {
                      return (
                        <TableCell
                          style={{
                            fontWeight: 'bold',
                            backgroundColor: '#e8eaf6',
                          }}
                          key={i}
                        >
                          {column}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody style={{ width: 'auto' }}>
                  {data.map((row, i: number) => {
                    return (
                      <TableRow key={i} id="TRow">
                        {Object.keys(row).map((column: string) => {
                          return <TableCell id="TColumn">{row[column]}</TableCell>;
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <div>No Data to display</div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      {alert && (
        <NotificationDialog
          onCloseAlert={() => setAlert(null)}
          severity={alert.severity}
          testMessage={alert.message}
          openAlert={!!alert}
        />
      )}
    </>
  );
}

export default SampleRecordPopUp;
